import { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader, Card, StatCard } from "@/components/common";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell,
  AreaChart, Area, CartesianGrid,
} from "recharts";
import { GraduationCap, CheckCircle2, Timer, Briefcase, Coffee, TrendingUp } from "lucide-react";

const COLORS = ["#1F5FA8", "#6FB63F", "#F08A1D", "#ef4444", "#8b5cf6", "#14b8a6"];
const FUNNEL_COLORS = ["#1F5FA8", "#2f74c0", "#6FB63F", "#F08A1D", "#a855f7", "#16a34a"];

export default function Analytics() {
  const [a, setA] = useState(null);
  useEffect(() => { api.get("/analytics").then(({ data }) => setA(data)); }, []);
  if (!a) return null;

  const toData = (obj) => Object.entries(obj || {}).map(([name, value]) => ({ name, value }));
  const trend = Object.entries(a.placement.trend_by_month || {}).map(([m, value]) => ({
    name: new Date(`${m}-01`).toLocaleDateString(undefined, { month: "short", year: "2-digit" }), value,
  }));

  return (
    <div data-testid="analytics-page">
      <PageHeader title="Leadership Analytics & ROI" subtitle="The placement-to-hire story across schools, facilities, touchpoints & recruiting" />

      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4 mb-6">
        <StatCard label="Total Requests" value={a.placement.total_requests} icon={GraduationCap} tone="blue" />
        <StatCard label="Placement Rate" value={`${a.placement.success_rate}%`} icon={CheckCircle2} tone="green" />
        <StatCard label="Avg Facility Response" value={`${a.facility.avg_response_days}d`} icon={Timer} tone="orange" />
        <StatCard label="Touchpoints Done" value={a.touchpoints.completed} icon={Coffee} tone="blue" />
        <StatCard label="Hires" value={a.recruiting.hired} icon={Briefcase} tone="green" />
        <StatCard label="Request → Hire" value={`${a.conversion.request_to_hire_rate}%`} icon={TrendingUp} tone="orange" />
      </div>

      {/* ROI conversion funnel */}
      <Card title="ROI Conversion Funnel — Request to Hire" className="mb-6" data-testid="roi-funnel">
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={a.conversion.funnel} layout="vertical" margin={{ left: 40 }}>
            <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#eee" />
            <XAxis type="number" fontSize={12} allowDecimals={false} />
            <YAxis type="category" dataKey="stage" fontSize={12} width={150} />
            <Tooltip />
            <Bar dataKey="value" radius={[0, 6, 6, 0]} label={{ position: "right", fontSize: 12 }}>
              {a.conversion.funnel.map((e, i) => <Cell key={i} fill={FUNNEL_COLORS[i % FUNNEL_COLORS.length]} />)}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
        <p className="text-xs text-zinc-400 mt-2">
          Of {a.placement.total_requests} requests, {a.conversion.funnel[1].value} were placed and {a.recruiting.hired} converted to hires
          ({a.conversion.placed_to_hire_rate}% of placed students hired).
        </p>
      </Card>

      <div className="grid md:grid-cols-2 gap-6">
        <Card title="Placement Volume Over Time">
          {trend.length === 0 ? <Empty /> : (
            <ResponsiveContainer width="100%" height={240}>
              <AreaChart data={trend}>
                <defs>
                  <linearGradient id="vol" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#1F5FA8" stopOpacity={0.4} />
                    <stop offset="100%" stopColor="#1F5FA8" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                <XAxis dataKey="name" fontSize={12} /><YAxis fontSize={12} allowDecimals={false} /><Tooltip />
                <Area type="monotone" dataKey="value" stroke="#1F5FA8" strokeWidth={2} fill="url(#vol)" />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </Card>

        <Card title="Requests by Discipline">
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={toData(a.placement.by_discipline)}>
              <XAxis dataKey="name" fontSize={12} /><YAxis fontSize={12} allowDecimals={false} /><Tooltip />
              <Bar dataKey="value" fill="#1F5FA8" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Card>

        <Card title="Placement Status Breakdown">
          <ResponsiveContainer width="100%" height={240}>
            <PieChart>
              <Pie data={toData(a.placement.by_status)} dataKey="value" nameKey="name" outerRadius={90} label>
                {toData(a.placement.by_status).map((e, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </Card>

        <Card title="Student Engagement (Touchpoints)">
          <div className="grid grid-cols-3 gap-4 text-center mb-4">
            <div><div className="text-2xl font-mono font-semibold text-cc-blue">{a.touchpoints.total}</div><div className="text-xs text-zinc-400">Total</div></div>
            <div><div className="text-2xl font-mono font-semibold text-cc-green">{a.touchpoints.completed}</div><div className="text-xs text-zinc-400">Completed</div></div>
            <div><div className="text-2xl font-mono font-semibold text-cc-orange">{a.touchpoints.gift_cards_sent}</div><div className="text-xs text-zinc-400">Gift Cards Sent</div></div>
          </div>
          {Object.keys(a.touchpoints.by_type || {}).length > 0 && (
            <ResponsiveContainer width="100%" height={150}>
              <BarChart data={toData(a.touchpoints.by_type)} layout="vertical">
                <XAxis type="number" fontSize={12} allowDecimals={false} /><YAxis type="category" dataKey="name" fontSize={11} width={120} /><Tooltip />
                <Bar dataKey="value" fill="#F08A1D" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </Card>

        <Card title="Video Calls">
          <div className="grid grid-cols-3 gap-4 text-center mb-4">
            <div><div className="text-2xl font-mono font-semibold text-cc-blue">{a.video?.total ?? 0}</div><div className="text-xs text-zinc-400">Total Calls</div></div>
            <div><div className="text-2xl font-mono font-semibold text-cc-green">{a.video?.completed ?? 0}</div><div className="text-xs text-zinc-400">Completed</div></div>
            <div><div className="text-2xl font-mono font-semibold text-cc-orange">{a.video?.adhoc ?? 0}</div><div className="text-xs text-zinc-400">Ad-hoc</div></div>
          </div>
          <div className="grid grid-cols-3 gap-4 text-center mb-4">
            <div><div className="text-xl font-mono font-semibold text-zinc-700">{a.video?.total_minutes ?? 0}</div><div className="text-xs text-zinc-400">Total Minutes</div></div>
            <div><div className="text-xl font-mono font-semibold text-zinc-700">{a.video?.avg_minutes ?? 0}</div><div className="text-xs text-zinc-400">Avg Length (min)</div></div>
            <div><div className="text-xl font-mono font-semibold text-zinc-700">{a.video?.longest_minutes ?? 0}</div><div className="text-xs text-zinc-400">Longest (min)</div></div>
          </div>
          {Object.keys(a.video?.by_status || {}).length > 0 && (
            <ResponsiveContainer width="100%" height={150}>
              <BarChart data={toData(a.video.by_status)} layout="vertical">
                <XAxis type="number" fontSize={12} allowDecimals={false} /><YAxis type="category" dataKey="name" fontSize={11} width={90} /><Tooltip />
                <Bar dataKey="value" fill="#6FB63F" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </Card>

        <Card title="Facility Responses">
          <div className="grid grid-cols-3 gap-4 text-center">
            <div><div className="text-2xl font-mono font-semibold text-cc-green">{a.facility.accepted}</div><div className="text-xs text-zinc-400">Answered Yes</div></div>
            <div><div className="text-2xl font-mono font-semibold text-red-500">{a.facility.denied}</div><div className="text-xs text-zinc-400">Answered No</div></div>
            <div><div className="text-2xl font-mono font-semibold text-cc-orange">{a.facility.pending}</div><div className="text-xs text-zinc-400">Pending</div></div>
          </div>
          {Object.keys(a.facility.denial_reasons || {}).length > 0 && (
            <div className="mt-4">
              <p className="text-xs uppercase text-zinc-400 mb-2">Denial reasons</p>
              {Object.entries(a.facility.denial_reasons).map(([k, v]) => (
                <div key={k} className="flex justify-between text-sm border-b last:border-0 py-1"><span>{k}</span><span className="font-mono">{v}</span></div>
              ))}
            </div>
          )}
        </Card>

        <Card title="Recruiting Pipeline">
          {Object.keys(a.recruiting.by_status || {}).length === 0 ? <Empty text="No recruiting records yet." /> : (
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={toData(a.recruiting.by_status)} layout="vertical">
                <XAxis type="number" fontSize={12} allowDecimals={false} /><YAxis type="category" dataKey="name" fontSize={11} width={130} /><Tooltip />
                <Bar dataKey="value" fill="#6FB63F" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </Card>
      </div>
    </div>
  );
}

function Empty({ text = "No data yet." }) {
  return <div className="h-[240px] flex items-center justify-center text-sm text-zinc-400">{text}</div>;
}
